/**
 * What to tell someone whose login text will never arrive.
 *
 * Twilio accepts a text before the carrier has seen it, so send-code answers
 * "sent" for a landline too, and the person waits on the code step for a code
 * that cannot come. Of the last 1,000 texts on 2026-09-23, 32 went to landlines
 * (30006) and 11 more were unreachable or did not exist -- and nothing ever told
 * any of them. Twilio learns within seconds, so the code step asks
 * (/api/auth/code-delivery) and says this.
 *
 * No imports: the login form uses it.
 * @see https://www.twilio.com/docs/api/errors
 */

/** Twilio's word on a text, reduced to what the code step can act on. */
export type CodeDelivery = 'pending' | 'delivered' | 'undeliverable' | 'unknown';

/** A text Twilio could not deliver, and could not with any retry to that number. */
const BY_TWILIO_CODE: Readonly<Record<number, string>> = {
  30006: "That number is a landline, so it can't get texts. Use a mobile number, or sign in with your email.",
  30005: "That number doesn't exist. Check the digits, or sign in with your email.",
  30003: "We couldn't reach that phone. Check it's on and has signal, then send a new code. Or sign in with your email.",
  30004: "That number blocks our texts. Use another number, or sign in with your email.",
};

export const UNDELIVERABLE_SMS_MESSAGE =
  "We couldn't deliver a text to that number. Use another number, or sign in with your email.";

/** Every message this module can say, so the code step can tell them from other errors. */
export const UNDELIVERABLE_SMS_MESSAGES: ReadonlySet<string> = new Set([
  ...Object.values(BY_TWILIO_CODE),
  UNDELIVERABLE_SMS_MESSAGE,
]);

/**
 * Codes that are a fact about the number the person typed -- a landline, or no
 * such number -- so no fix of ours could have got the text there. The rest
 * (unreachable, blocked, carrier filtering) can be about our sender.
 */
const NUMBER_CANNOT_TEXT: ReadonlySet<number> = new Set([30005, 30006]);

/** True when Twilio's code says the number itself can never get a text. */
export function isNumberThatCannotText(smsErrorCode?: number): boolean {
  return smsErrorCode !== undefined && NUMBER_CANNOT_TEXT.has(smsErrorCode);
}

/** Twilio statuses for a text still on its way. `none` is a text Twilio has not listed yet. */
const IN_FLIGHT: ReadonlySet<string> = new Set(['none', 'accepted', 'scheduled', 'queued', 'sending', 'sent']);

/**
 * Read Twilio's status for a login text.
 *
 * `sent` stays `pending`: it means a carrier took it, and some never send a
 * receipt, so it is not proof of delivery -- only `delivered` is.
 */
export function classifySmsDelivery(
  smsStatus: string,
  smsErrorCode?: number
): { delivery: CodeDelivery; message?: string } {
  if (smsStatus === 'delivered') return { delivery: 'delivered' };
  if (smsStatus === 'undelivered' || smsStatus === 'failed') {
    const message = (smsErrorCode !== undefined && BY_TWILIO_CODE[smsErrorCode]) || UNDELIVERABLE_SMS_MESSAGE;
    return { delivery: 'undeliverable', message };
  }
  if (IN_FLIGHT.has(smsStatus)) return { delivery: 'pending' };
  return { delivery: 'unknown' };
}
